import './dialog.less'
import _ from "lib/underscore";
import ko from "lib/knockout";
import {newLogger} from "../utils/logger";
import {checkIsIframe, isMobile} from "../utils/utils";
import {asString} from "../utils/var";

const logger = newLogger('ZDialog');

/**
 * Creates new dialog with IFrame with given url
 * @param {string} url IFrame url
 * @param {object} options ZDialog Options. See {@link ZDialog.defaults}
 * @return {ZDialog}
 */
export function createIframeDialog(url, options = null) {
	return newDialog({content: url, type: 'iframe', __width: 800, ...options});
}

/**
 * Creates new dialog instance.
 * autoOpen option is false by default, so you should either call open() method
 * or override autoOpen option
 * @param {*} content HTML content. Can be either valid HTML string, or DOM node, or jQuery object
 * @param {object} options ZDialog Options. See {@link ZDialog.defaults}
 * @return {ZDialog} Dialog instance
 */
export function createHtmlDialog(content, options = null) {
	return newDialog({content, type: 'html', showCloseButton: true, ...options});
}

/**
 * The same as {@link createHtmlDialog()} but with autoOpen enabled
 * @param {*} content HTML content
 * @param {object} options ZDialog options
 * @return {ZDialog} Dialog instance
 */
export function openHtmlDialog(content, options = null) {
	return createHtmlDialog(content, {autoOpen: true, ...options});
}

/**
 * Creates new dialog as new window with given url
 * @param {string} url IFrame url
 * @param {object} options ZDialog Options. See {@link ZDialog.defaults}
 * @return {ZDialog}
 */
export function createWindowDialog(url, options = null) {
	return newDialog({content: url, type: 'window', ...options});
}

// add some helper functions to jQuery
$.extend($.fn, {
	/**
	 * Calls {@link openHtmlDialog()} with source element as content
	 * @param {object} options ZDialog Options. See {@link ZDialog.defaults}
	 */
	zdialog: function (options = null) {
		const $el = $(this);
		const $placeholder = $('<div style="display: none !important;"></div>').insertAfter($el);
		const isInDom = $el.closest(document.documentElement);
		const isVisible = $el.css('display') != 'none';
		if (isInDom) {
			$el.detach();
		}
		if (!isVisible) {
			$el.show();
		}
		const dialog = createHtmlDialog($el, options);
		dialog.onBeforeClose(() => {
			$el.detach();
		});
		dialog.onClose(() => {
			if (isInDom) {
				$el.insertAfter($placeholder);
			}
			if (!isVisible) {
				$el.hide();
			}
			$placeholder.remove();
		});
		dialog.open();
		$el.data('zdialogInstance', dialog);
		return this;
	},

	/**
	 * Returns ZDialog instance for given element (if the dialog was created with {@link jQuery.fn.zdialog} function)
	 * @return {ZDialog}
	 */
	zdialogInstance: function () {
		return this.data('zdialogInstance');
	}
});

// FOR TESTING
/*import {MODAL_ASK_REDIRECT, MODAL_CRITICAL_NEWS, MODAL_LOGIN, MODAL_PACKAGE_INFO, MODAL_REVIEW} from "../modals";
$('.logo').click(function(e) {
	e.preventDefault();
	const queue = [];
	const showNext = () => {
		if (queue.length > 0) {
			queue.shift()();
		}
	};
	const onOpen = () => {};
	// queue.push(() => MODAL_REVIEW.show(104625).onOpen(showNext));
	// queue.push(() => MODAL_LOGIN.show().onOpen(showNext));
	// queue.push(() => MODAL_PACKAGE_INFO.show(1803110691).onOpen(showNext));
	// queue.push(() => MODAL_ASK_REDIRECT.show().onOpen(showNext));
	// queue.push(() => MODAL_CRITICAL_NEWS.show(312).onOpen(showNext));
	queue.push(() => openHtmlDialog('<div rel="show-next" style="width: 400px; height: 400px; background: red;"></div>').onOpen(onOpen));
	queue.push(() => openHtmlDialog('<div style="width: 400px; height: 1000px; background: red;"></div>').onOpen(onOpen));
	queue.push(() => openHtmlDialog('<div style="width: 1500px; height: 400px; background: red;"></div>').onOpen(onOpen));
	queue.push(() => openHtmlDialog('<div style="width: 400px; height: 400px; background: red;"></div>', {fullscreen: true}).onOpen(onOpen));
	queue.push(() => openHtmlDialog('<div style="width: 2500px; height: 2500px; background: red;"></div>', {fullscreen: true}).onOpen(onOpen));
	showNext();

	$(document).on('click', '.zdialog-html > div', showNext);
});*/

/**
 * @param {object} options
 * @return {ZDialog}
 */
let newDialog = function (options) {
	return new ZDialog(options);
};

// force use newDialog function from top window
const key = '__zdialog_newDialog';
if (checkIsIframe()) {
	newDialog = window.top[key];
} else {
	window.top[key] = newDialog;
}

/**
 * Dialog class
 */
class ZDialog {
	static defaults = {
		name: '',
		type: 'iframe', // iframe, html
		content: '',
		title: '',
		buttons: [],
		modal: false,
		autoOpen: false,
		showCloseButton: false,

		width: '',
		height: '',
		fullscreen: false,
		cssClass: '',

		custom: null,

		//events
		onOpen: null,
		onClose: null
	};

	// options
	_width = ko.observable();
	_height = ko.observable();
	_isModal = ko.observable(false);
	_isCloseBtnVisible = ko.observable(false);

	_$el;
	_$iframe;
	_wnd;
	_isLoading = ko.observable(false);
	_isOpened = ko.observable(false);
	_isClosed = ko.observable(false);
	_isHidden = ko.observable(false);
	_contentHeight = ko.observable(0);
	_iframeHeight = ko.observable(0);
	/** @type {HeightWatcher} */
	_heightWatcher;
	_opened = false;
	/** @type {JQuery.Callbacks} */
	_onOpen = $.Callbacks();
	/** @type {JQuery.Callbacks} */
	_onClose = $.Callbacks();
	/** @type {JQuery.Callbacks} */
	_onBeforeClose = $.Callbacks();
	_result = null;

	constructor(options) {
		const o = {
			...ZDialog.defaults,
			...options
		};

		this._name = o.name;
		this._o = o;
		this._isModal(!!o.modal);
		this._isCloseBtnVisible(!!o.showCloseButton);
		this._width(o.width);
		this._height(o.height);

		if (o.onOpen) {
			this._onOpen.add(o.onOpen);
		}
		if (o.onClose) {
			this._onClose.add(o.onClose);
		}

		if (o.autoOpen) {
			this.open();
		}
	}

	open() {
		if (this.isOpened) {
			return;
		}
		this._isClosed(false);
		this._isHidden(false);
		this._result = null;
		if (this.type === 'window') {
			this._wnd = window.open(this.content);
			this._wnd.addEventListener('DOMContentLoaded', () => this._handleWindowReady());
			this._wnd.onbeforeunload = () => {
				if (!this.isClosed) {
					this.close();
				}
			};
			this._isLoading(true);
		} else if (!dialogManager.isRegistered(this)) {
			if (this.isFullScreen) {
				this._width('');
				this._height('');
				this._contentHeight('');
				this._iframeHeight('');
			}
			if (this.type === 'iframe') {
				this._isLoading(true);
				if (!this.isAutoHeight) {
					this._contentHeight(this.height);
					this._iframeHeight(this.height);
				} else {
					this._contentHeight('');
				}
			} else {
				this._contentHeight(this.height);
				this._isOpened(true);
				setTimeout(() => this._onOpen.fireWith(this), 0);
			}
			dialogManager.register(this);
		} else if (this._isHidden()) {
			dialogManager.show(this);
		}
	}

	close() {
		if (this.isClosed) {
			return;
		}
		this._onBeforeClose.fireWith(this, [this._result]);
		this._isOpened(false);
		this._isHidden(false);
		this._isClosed(true);
		if (this.type === 'window') {
			if (this._wnd) {
				this._wnd.close();
				window.top.focus();
				this._wnd = null;
				setTimeout(() => this._onClose.fireWith(this, [this._result]), 0);
			}
		} else if (dialogManager.isRegistered(this)) {
			if (this._heightWatcher) {
				this._heightWatcher.off();
			}
			dialogManager.unregister(this);
			setTimeout(() => this._onClose.fireWith(this, [this._result]), 0);
		}
	}

	hide() {
		if (this.type === 'window') {
			logger.error('Method is not supported for current dialog type');
		} else if (dialogManager.isRegistered(this)) {
			this._isOpened(false);
			this._isHidden(true);
			dialogManager.hide(this);
		}
	}

	closeWithResult(result) {
		this.result = result;
		this.close();
	}

	updatePosition() {
		if (this.type === 'iframe') {
			this._contentHeight(this.iframeHeight);
		}
	}

	/** @return {string} */
	get name() {
		return this._name;
	}

	/** @return {string} */
	get type() {
		return this._o.type;
	}

	/** @return {string} */
	get content() {
		return this._o.content;
	}

	/** @return {string} */
	get title() {
		return this._o.title;
	}

	/** @return {number} */
	get width() {
		return this._width();
	}

	/** @return {number|string} */
	get height() {
		return this._height();
	}

	/** @return {number|string} */
	get minWidth() {
		return this._o.minWidth;
	}

	/** @return {number|string} */
	get maxWidth() {
		return this._o.maxWidth;
	}

	/** @return {number|string} */
	get contentWidth() {
		return this._width();
	}

	/** @return {number|string} */
	get contentHeight() {
		return this._contentHeight();
	}

	/** @return {number} */
	get iframeHeight() {
		return this._iframeHeight();
	}

	/** @return {boolean} */
	get isLoading() {
		return this._isLoading();
	}

	/** @return {boolean} */
	get isOpened() {
		return this._isOpened();
	}

	/** @return {boolean} */
	get isClosed() {
		return this._isClosed();
	}

	/** @return {boolean} */
	get isHidden() {
		return this._isHidden();
	}

	/** @return {boolean} */
	get isModal() {
		return this._isModal();
	}

	/** @return {boolean} */
	get isCloseBtnVisible() {
		return this._isCloseBtnVisible();
	}

	/** @type {boolean} */
	get isFullScreen() {
		return this._o.fullscreen;
	}

	/** @type {boolean} */
	get isAutoHeight() {
		return asString(this.height) === '' || this.height === 'auto';
	}

	/** @return {*} */
	get result() {
		return this._result;
	}

	/**
	 * @param {*} value
	 */
	set result(value) {
		this._result = value;
	}

	/**
	 * @return {*}
	 */
	get customData() {
		return this._o.custom;
	}

	/**
	 * @return {string}
	 */
	get cssClass() {
		return asString(this._o.cssClass);
	}

	handleIframeLoad(item, e) {
		logger.log('loaded', e);
		this._isLoading(false);

		if (!this.isFullScreen && this.isAutoHeight) {
			this._heightWatcher = new HeightWatcher(e.target, (newHeight) => {
				logger.log(`new height = ${newHeight}`);
				this._iframeHeight(newHeight);
				if (!this.contentHeight) {
					this._contentHeight(newHeight);
				}
				this._isOpened(true);
				setTimeout(() => this._onOpen.fireWith(this), 0);
			});
		}
		else {
			this._isOpened(true);
			setTimeout(() => this._onOpen.fireWith(this), 0);
		}

		// inject this instance to iframe
		e.target.contentWindow.dialogInstance = this;

		// apply special classes
		$(e.target.contentWindow.document.body)
			.toggleClass('zdialog-layout-modal', this.isModal)
			.toggleClass('zdialog-layout-autoheight', this.isAutoHeight)
			.toggleClass('zdialog-layout-fullscreen', this.isFullScreen);

		// triger event inside iframe
        e.target.contentWindow.$(e.target.contentWindow.document).trigger('zdialog-shown');
	}

	_handleWindowReady(e) {
		logger.log('loaded', e);
		this._isLoading(false);
		this._isOpened(true);

		// inject this instance to iframe
		this._wnd.dialogInstance = this;
		$(this._wnd.document.body).addClass('zdialog-layout-window');

		setTimeout(() => this._onOpen.fireWith(this), 0);
	}

	handleCloseClick(item, e) {
		const $target = $(e.target);
		if ($target.is('[data-dialog-result]')) {
			this.result = $target.data('dialogResult');
		}
		this.close();
	}

	handleBackdropClick() {
		if (dialogManager.isTopmost(this) && !this.isModal) {
			this.close();
		}
	}

	onOpen(callback) {
		this._onOpen.add(callback);
	}

	offOpen(callback) {
		this._onOpen.remove(callback);
	}

	onClose(callback) {
		this._onClose.add(callback);
	}

	offClose(callback) {
		this._onClose.remove(callback);
	}

	onBeforeClose(callback) {
		this._onBeforeClose.add(callback);
	}

	offBeforeClose(callback) {
		this._onBeforeClose.remove(callback);
	}
}

class DialogManager {
	_counter = 0;
	/** @type {KnockoutObservableArray<ZDialog>} */
	_dialogs = ko.observableArray([]);
	_$el;
	_isClosing = ko.observable(false);
	_initialized = false;
	_timeout;

	_wrapper = new WindowWrapper();

	constructor() {
		this._$html = $('html');
		this._$body = $('body');

		this._isLoading = ko.pureComputed(() => {
		    return _.any(this.dialogs, d => d.isLoading);
		});

		this._visibleDialogs = ko.pureComputed(() => {
		    return this.dialogs.filter(d => !d.isHidden);
		});
	}

	/**
	 * @param {ZDialog} dialog
	 */
	register(dialog) {
		if (this.isRegistered(dialog)) {
			return;
		}

		this._dialogs.push(dialog);
		this._checkMarkupAfterOpen();
	}

	/**
	 * @param {ZDialog} dialog
	 */
	unregister(dialog) {
		if (this._dialogs.remove(dialog).length === 0) {
			// nothing has been removed
			return;
		}
		this._checkMarkupAfterClose();
	}

	/**
	 * @param {ZDialog} dialog
	 */
	hide(dialog) {
		if (this.isRegistered(dialog)) {
			this._checkMarkupAfterClose();
		}
	}

	/**
	 * @param {ZDialog} dialog
	 */
	show(dialog) {
		if (this.isRegistered(dialog)) {
			this._checkMarkupAfterOpen();
		}
	}

	_checkMarkupAfterOpen() {
		if (this._isClosing()) {
			clearTimeout(this._timeout);
			this._timeout = null;
			this._isClosing(false);
		} else if (this.visibleDialogs.length === 1 && !this._initialized) {
			this._wrapper.on();

			if (!this._$el) {
				this._$el = $(`<div class="zdialog-wrapper" data-bind="component: {name: 'zdialog-wrapper', params: $data}, 
										css: {'zdialog-loading': isLoading, 'zdialog-closing': isClosing}"></div>`);
				ko.applyBindings(this, this._$el[0]);
				this._$el.appendTo('body');
			} else {
				this._$el.show();
			}
			this._initialized = true;
		}
	}

	_checkMarkupAfterClose() {
		if (!this._isClosing() && this.visibleDialogs.length === 0 && this._initialized) {
			// no dialogs left
			this._isClosing(true);
			this._timeout = setTimeout(() => {
				this._wrapper.off();
				// logger.log('remove container');
				this._$el.remove();
				ko.cleanNode(this._$el[0]);
                this._$el = null;
				this._initialized = false;
				this._isClosing(false);
			}, 300);
		}
	}

	/**
	 * @param {ZDialog} dialog
	 * @return {boolean}
	 */
	isRegistered(dialog) {
		return _.contains(this.dialogs, dialog);
	}

	handleBeforeRemove(node) {
		const $node = $(node);
		$node.remove();
		/*if (node.nodeType === 3) {
			$node.remove();
			return;
		}
		$node.addClass('zdialog-closing');
		setTimeout(() => $node.remove(), 300);*/
	}

	/** @return {ZDialog[]} */
	get dialogs() {
		return this._dialogs();
	}

	/** @return {ZDialog[]} */
	get visibleDialogs() {
		return this._visibleDialogs();
	}

	/** @return {boolean} */
	get isLoading() {
		return this._isLoading();
	}

	/** @return {boolean} */
	get isClosing() {
		return this._isClosing();
	}

	getTopmostDialog() {
		return _.last(this.visibleDialogs.filter(d => !d.isLoading));
	}

	isTopmost(dialog) {
		return dialog === this.getTopmostDialog();
	}

	/**
	 * @param {ZDialog} dialog
	 */
	getDialogClass(dialog) {
		const css = [];
		if (dialog.isModal) {
			css.push('zdialog-modal');
		}
		if (dialog.isFullScreen) {
			css.push('zdialog-fullscreen');
		}
		if (this.isTopmost(dialog)) {
			css.push('zdialog-topmost');
		} else if (this.visibleDialogs.length > 1) {
			css.push('zdialog-overlapped');
		}
		if (dialog.isLoading) {
			css.push('zdialog-loading');
		}
		if (dialog.isOpened) {
			css.push('zdialog-opened');
		}
		if (dialog.isHidden) {
			css.push('zdialog-hidden');
		}
		if (dialog.name) {
			css.push('zdialog-instance-' + dialog.name);
		}
		if (dialog.cssClass) {
			css.push(dialog.cssClass);
		}
		return css.join(' ');
	}
}

class HeightWatcher {
	_currHeight = 0;
	_event = $.Callbacks();

	constructor($iframe, callback) {
		this._event.add(callback);
		this._ibody = $($iframe)[0].contentWindow.document.body;
        // this.updateHeight();
		setTimeout(() => {
			this.updateHeight();
			this._interval = setInterval(() => this.updateHeight(), 50);
		}, 0);
	}

	updateHeight() {
		// const newHeight = this._ibody.offsetHeight;
		const newHeight = this.getHeight();
		if (newHeight !== this._currHeight) {
			this._currHeight = newHeight;
			this._event.fire(newHeight);
		}
	}

	getHeight() {
		let height = this._ibody.scrollHeight;
		$(this._ibody).find('iframe:visible').filter(function () {
			return $(this).css('visibility') !== 'hidden';
		}).each(function() {
			let iHeight = $(this).height();
			if (iHeight > height) {
				height = iHeight;
			}
		});
		return height;
	}

	off() {
		clearInterval(this._interval);
		this._event.empty();
	}
}

class WindowWrapper {
	_$wnd = $(window);
	_$html = $('html');
	_$head = $('head');
	_scrollTop = 0;
	_scrollLeft = 0;

	constructor() {
		this._$scrollBarStyle = $(`<style type="text/css"></style>`).appendTo(this._$head);
		this._$positionStyle = $(`<style type="text/css"></style>`).appendTo(this._$head);
		// this._useFixedLayout = /(iPod|iPhone|iPad)/.test(navigator.userAgent);
		this._useFixedLayout = isMobile();///(iPod|iPhone|iPad)/.test(navigator.userAgent);
	}

	on() {
		if (this._useFixedLayout) {
			this._scrollTop = this._$wnd.scrollTop();
			this._scrollLeft = this._$wnd.scrollLeft();
			this._applyPositionStyle();
			this._$wnd.scrollTop(0).scrollLeft(0);
		}
		this._applyScrollbarStyle();

		this._$html
			.toggleClass('zdialog-scrollbar-fix', this._hasScrollBar())
			.toggleClass('zdialog-position-fix', this._useFixedLayout)
			.addClass('zdialog-open');
	}

	off() {
		this._$html
			.removeClass('zdialog-scrollbar-fix')
			.removeClass('zdialog-position-fix')
			.removeClass('zdialog-open');
		if (this._useFixedLayout) {
			this._$wnd.scrollTop(this._scrollTop).scrollLeft(this._scrollLeft);
		}
	}

	_applyScrollbarStyle() {
		const scrollBarWidth = getScrollBarWidth();
		// language=CSS
		this._$scrollBarStyle.html(`
.zdialog-scrollbar-fix {
	padding-right: ${scrollBarWidth}px;
}
`);
	}

	_applyPositionStyle() {
		// language=CSS
		this._$positionStyle.html(`
.zdialog-position-fix {
	position: fixed;
	width: 100%;
	height: 100%;
	top: -${this._scrollTop}px;
	left: -${this._scrollLeft}px;
}
`);
	}

	_hasScrollBar() {
		return hasScrollBar(this._$html);
	}
}

function getScrollBarWidth() {
	const $outer = $('<div>').css({visibility: 'hidden', width: 100, overflow: 'scroll'}).appendTo('body'),
		widthWithScroll = $('<div>').css({width: '100%'}).appendTo($outer).outerWidth();
	$outer.remove();
	return 100 - widthWithScroll;
}

function hasScrollBar(el) {
	const $el = $(el);
	return $el.length > 0 ? $el[0].scrollHeight > $(window).height() : false;
}

export const dialogManager = new DialogManager();

ko.components.register('zdialog-wrapper', {
	template: `
<div class="zdialog-overlay"></div>
<div class="zdialog-loader"></div>
<div data-bind="foreach: {data: dialogs, beforeRemove: handleBeforeRemove}">
	<div class="zdialog-container">
		<div class="zdialog" data-bind="component: {name: 'zdialog', params: $data}, height: contentHeight, 
										css: $parent.getDialogClass($data)"></div>
	</div>
</div>
`.trim()
});
ko.components.register('zdialog', {
	template: `
<div class="zdialog-backdrop" data-bind="click: handleBackdropClick"></div>
<div class="zdialog-content" data-bind="css: 'zdialog-content-' + type, 
										component: {name: 'zdialog-content-' + type, params: $data},
										width: contentWidth"></div>
`.trim()
});
ko.components.register('zdialog-content-iframe', {
	template: `
<div class="zdialog-close-btn" title="Закрыть" data-bind="click: handleCloseClick, visible: isCloseBtnVisible, clickBubble: false"></div>
<iframe class="zdialog-iframe" frameborder="0" vspace="0" hspace="0" scrolling="auto"
		data-bind="attr: {src: content}, event: {load: handleIframeLoad}, height: iframeHeight"></iframe>
`.trim()
});
ko.components.register('zdialog-content-html', {
	template: `
<!-- ko if: title -->
<h2 class="zdialog-title">
	<span data-bind="html: title"></span>
	<div class="zdialog-close-btn" title="Закрыть" data-bind="click: handleCloseClick, visible: isCloseBtnVisible, clickBubble: false"></div>
</h2>
<!-- /ko -->
<!-- ko ifnot: title -->
<div class="zdialog-close-btn" title="Закрыть" data-bind="click: handleCloseClick, visible: isCloseBtnVisible, clickBubble: false"></div>
<!-- /ko -->
<div class="zdialog-html" data-bind="html: content, on: {events: 'click', selector: '[rel=close-dialog]', handler: handleCloseClick}"></div>
`.trim()
});



// WEBPACK FOOTER //
// ./core/widgets/dialog.js